import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { createSignedAssetUrl } from '../../lib/storage';
import type { CommunityTemplateGalleryRead, CommunityTemplateRead } from '../../types/api';
import { COMMUNITY_TEMPLATE_STATE_KEY, ownCommunityTemplatePin } from './communityTemplatePrefill';

export interface StartFromOwnTemplateOptions {
  /**
   * Receives the pin instead of a navigation: the brief itself passes this, so
   * a template picked on the brief is pinned in place. Without it, `start`
   * opens `/generation` with the pin in router state.
   */
  onPin?: (pin: CommunityTemplateGalleryRead) => void;
}

export interface StartFromOwnTemplateResult {
  /** Pins the brief to this template, whatever its status. */
  start: (template: CommunityTemplateRead) => Promise<void>;
  /** The template being handed over, while its design is signed. */
  startingId: string | null;
}

/**
 * One of the member's own templates, in the shape the brief pins, with its
 * private design signed.
 *
 * An image design is a private upload, so it is signed through the same cached
 * helper `useSignedAssetUrl` uses: a card that already shows the design costs
 * no second request. A signing failure does not stop the brief: the pin draws
 * the design without its backdrop, and the post itself is rendered server-side
 * from the template id either way.
 */
export async function signedOwnTemplatePin(
  template: CommunityTemplateRead,
): Promise<CommunityTemplateGalleryRead> {
  let imageUrl: string | null = null;
  if (template.image_path) {
    try {
      imageUrl = await createSignedAssetUrl(template.image_path);
    } catch {
      imageUrl = null;
    }
  }
  return ownCommunityTemplatePin(template, imageUrl);
}

/**
 * Starts a brief on one of the member's own community templates — the same
 * hand-off a gallery tile makes, router state under
 * `COMMUNITY_TEMPLATE_STATE_KEY`, so the brief has one way in. The brief's own
 * picker passes `onPin` and receives the identical pin without leaving the page.
 */
export function useStartFromOwnTemplate(
  options: StartFromOwnTemplateOptions = {},
): StartFromOwnTemplateResult {
  const { onPin } = options;
  const navigate = useNavigate();
  const [startingId, setStartingId] = useState<string | null>(null);

  async function start(template: CommunityTemplateRead): Promise<void> {
    setStartingId(template.id);
    const pin = await signedOwnTemplatePin(template);
    setStartingId(null);
    if (onPin) {
      onPin(pin);
      return;
    }
    void navigate('/generation', {
      state: { [COMMUNITY_TEMPLATE_STATE_KEY]: pin },
    });
  }

  return { start, startingId };
}
